import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ColorNordicGrey1, ColorSystemNegative2, Spacing8 } from 'autogen/design-tokens/tokens';
import type { ProductSubscriptionInfoViewModel, SubscriptionPriceInfo } from 'autogen/swagger/all';
import { NotClubMatasPrice, PriceBefore } from 'autogen/translation-keys/trans-website-product-priceinfo';
import {
    BuyOnSubscription,
    PriceHereafter,
    SubscriptionCombinedUsp,
    SubscriptionCombinedUspNoSaving,
    SubscriptionMinimumQuantityReminderPDPWarningText
} from 'autogen/translation-keys/trans-website-product-subscription';
import { SubscriptionUSP } from 'components';
import Card from 'components/generic/card';
import { Typography } from 'components/generic/typography';
import { SubscriptionContext } from 'components/pages/PDP/components/MidSection/hooks/context';
import { useProductStore } from 'components/pages/PDP/shared/productStore';
import { format } from 'modules/helpers/strings';
import { GA4Tracking } from 'modules/tracking/GA4/GA4Tracking';
import { inputValues } from '../shared/enums';
import { AdditionalLine, Content, Headline, Label, LineThrough, Price, StyledCard, StyledRadio } from './components';
import AboutSubscriptionButton from './components/AboutSubscriptionButton';
import PriceExplainer from './components/PriceExplainer';
import FrequencySelection from './components/SubscriptionFrequencySelection/FrequencySelection';
import RadioGroup from './components/SubscriptionFrequencySelection/RadioGroup';

const AdditionalPriceInfo = styled.div`
    grid-column: -1/1;
`;

const Ruler = styled.span`
    display: block;
    width: 100%;
    border-bottom: 1px solid ${ColorNordicGrey1};
    margin-top: ${Spacing8};
    margin-bottom: ${Spacing8};
`;

const WarningText = styled(Typography.Body)`
    color: ${ColorSystemNegative2};
    margin-bottom: ${Spacing8};
`;

const FrequencyLabel = styled(Typography.Body)`
    margin-bottom: ${Spacing8};
`;

function useSubscriptionUspText(subscriptionPriceInfo: SubscriptionPriceInfo): string {
    const { discountType, fixedDiscountAmount, discountPercentage } = subscriptionPriceInfo;
    const { t } = useTranslation();

    switch (discountType) {
        case 'DiscountPercentage':
            return format(t(SubscriptionCombinedUsp))(`${discountPercentage * 100}%`);
        case 'FixedDiscountByQuantity':
        case 'FixedDiscountByAmount':
            return format(t(SubscriptionCombinedUsp))(fixedDiscountAmount);
        default:
            return t(SubscriptionCombinedUspNoSaving);
    }
}

export default function SubscriptionCard(model: ProductSubscriptionInfoViewModel): JSX.Element {
    const {
        priceInfo: { onDiscount, priceLabel },
        subscriptionInfoModel: { priceInfo: subPrice, frequencyInfo, minimumQuantity, infoLinkAction },
        subscriptionPriceInfo,
        subscriptionPriceInfo: {
            showSubscriptionIntroPrice,
            subscriptionPriceBefore,
            subscriptionSubsequentlyPrice,
            currentPrice
        }
    } = model;
    const { choosable } = frequencyInfo;
    const { subscriptionActive, setSubscriptionActive } = useContext(SubscriptionContext);
    const { setIsSubscription } = useProductStore();
    const { t } = useTranslation();
    const isClubMatasOnlyPrice = priceLabel?.type === 'ClubMatasOnlyPrice';
    const priceBeforeLabel = isClubMatasOnlyPrice ? t(NotClubMatasPrice) : t(PriceBefore);
    const showPriceBefore = onDiscount || showSubscriptionIntroPrice;
    const firstFrequencyGroup = choosable[0];

    function switchSelection(type: inputValues) {
        setSubscriptionActive(type === inputValues.subscription);
        setIsSubscription(type === inputValues.subscription);

        GA4Tracking({
            eventName: 'toggle_subscription',
            context: 'product_page',
            category: 'ecommerce',
            eventParams: [{ param_name: 'toggle', param_value: type === inputValues.subscription }]
        });
    }

    return (
        <StyledCard forwardedAs="label" $isActive={subscriptionActive}>
            <Card.Content>
                <Headline $isActive={subscriptionActive} forwardedAs="div">
                    <StyledRadio
                        name="ProductPaymentChoice"
                        value={inputValues.subscription}
                        onChange={() => switchSelection(inputValues.subscription)}
                        defaultChecked={subscriptionActive}
                    />

                    <Label>{t(BuyOnSubscription)}</Label>

                    <Price>
                        {subPrice.priceLabel} {currentPrice}
                    </Price>

                    {subscriptionActive && (showPriceBefore || subPrice.showSubscriptionPrice) && (
                        <AdditionalPriceInfo>
                            {showPriceBefore && (
                                <>
                                    <AdditionalLine>
                                        <Typography.Body as="span">{priceBeforeLabel}</Typography.Body>
                                        <LineThrough as="span">{subscriptionPriceBefore}</LineThrough>
                                    </AdditionalLine>
                                    <Ruler />
                                </>
                            )}

                            {subPrice.showSubscriptionPrice && (
                                <>
                                    <AdditionalLine>
                                        <Typography.Body as="span" size="M" strong>
                                            {t(PriceHereafter)}
                                            {' - '}
                                            <PriceExplainer
                                                subscriptionPriceInfo={subscriptionPriceInfo}
                                                discountPercentage={subPrice.discountPercentage}
                                            />
                                        </Typography.Body>
                                        <Typography.Body as="span" size="M" strong>
                                            {subscriptionSubsequentlyPrice}
                                        </Typography.Body>
                                    </AdditionalLine>
                                    <Ruler />
                                </>
                            )}
                        </AdditionalPriceInfo>
                    )}
                </Headline>

                {subscriptionActive && (
                    <Content>
                        <FrequencyLabel size="M" strong forwardedAs="div">
                            {firstFrequencyGroup.label}
                        </FrequencyLabel>

                        {minimumQuantity > 1 && (
                            <WarningText forwardedAs="div">
                                {format(t(SubscriptionMinimumQuantityReminderPDPWarningText))(minimumQuantity)}
                            </WarningText>
                        )}

                        {choosable.length <= 1 && firstFrequencyGroup.frequencies.length <= 3 ? (
                            <RadioGroup {...frequencyInfo} />
                        ) : (
                            <FrequencySelection {...frequencyInfo} />
                        )}
                    </Content>
                )}

                <SubscriptionUSP usp={useSubscriptionUspText(subPrice)} />

                {subscriptionActive && <AboutSubscriptionButton linkAction={infoLinkAction} />}
            </Card.Content>
        </StyledCard>
    );
}
